import React, { useMemo, useRef, useState } from "react";
import { Select as OriginalSelect, theme } from "antd";
import { useNotifier } from "context/ContextNotifier";
import { usePromisesLoader } from "utils/usePromiseLoader";
import { SizeType } from "antd/es/config-provider/SizeContext";
import { useTranslation } from "react-i18next";
import { BaseSelectRef } from "rc-select";
import { debounce } from "../../../utils/common";
import styled from "styled-components/macro";
import { ALLOWED_HOUSE_COUNTRIES } from "../../../constants";

const { useToken } = theme;

export type PlacesAutocompleteOptionType = {
  value: string;
  label: string;
  data?: google.maps.places.AutocompletePrediction;
};

const Select = styled(OriginalSelect<string, PlacesAutocompleteOptionType>)<{
  $fontSize?: number;
}>`
  &.ant-select-lg {
    input[type="search"] {
      ${(p) =>
        p.$fontSize !== undefined ? `font-size: ${p.$fontSize}px;` : ""}
    }
  }
`;

type PlacesAutocompleteProps = {
  displayValue?: {
    location?: google.maps.LatLngLiteral;
    displayName: string;
  };
  onChange: (
    value: { lat: number; lng: number } | undefined,
    data: { placeId: string; name: string; description: string } | undefined
  ) => void;
  allowClear?: boolean;
  blurOnSelect?: boolean;
  error?: boolean;
  size?: SizeType;
  disabled?: boolean;
  placeholder?: string;
  countriesCodes?: string[];
  placesTypes?: string[];
};

const defaultCountriesCodes = ALLOWED_HOUSE_COUNTRIES.map((c) =>
  c.toLowerCase()
);

const defaultPlacesTypes = ["locality"];

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  displayValue,
  onChange,
  allowClear = true,
  blurOnSelect = false,
  size,
  error,
  disabled,
  placeholder,
  countriesCodes,
  placesTypes
}) => {
  const [options, setOptions] = useState<PlacesAutocompleteOptionType[]>([]);
  const [autocompleteService] = useState(() => {
    return new google.maps.places.AutocompleteService();
  });
  const [geocoder] = useState(() => {
    return new google.maps.Geocoder();
  });
  const notifier = useNotifier();
  const { watchPromise, isLoading } = usePromisesLoader();
  const [selectedPlace, setSelectedPlace] = useState<{
    placeId: string;
    description: string;
    location: google.maps.LatLngLiteral;
  } | null>();

  const selectRef = useRef<BaseSelectRef>(null);
  const lastSelectedPlaceIdRef = useRef<string>();
  const [showNotFoundPlaceholder, setShowNotFoundPlaceholder] = useState(false);
  const { t } = useTranslation("geomap");
  const { token: antToken } = useToken();

  const debouncedSearch = useMemo(() => {
    return debounce((search: string) => {
      if (search.length === 0) {
        setOptions([]);
        return;
      }

      watchPromise(
        autocompleteService
          .getPlacePredictions({
            input: search,
            types: placesTypes ?? defaultPlacesTypes,
            componentRestrictions: {
              country: countriesCodes ?? defaultCountriesCodes
            }
          })
          .then((response) => {
            setOptions(
              response.predictions.map((d) => ({
                value: d.place_id,
                label: d.description,
                data: d
              }))
            );
          })
          .catch((e) => {
            console.error(e);
            notifier.error({
              message: t("placesAutocomplete.problems")
            });
          })
      );
    }, 300);
  }, [
    countriesCodes,
    placesTypes,
    autocompleteService,
    notifier,
    watchPromise,
    t
  ]);

  const handleChange = (
    value: string | undefined,
    option: PlacesAutocompleteOptionType | PlacesAutocompleteOptionType[]
  ) => {
    if (value === undefined) {
      setSelectedPlace(null);
      onChange(undefined, undefined);
      return;
    }
    if (!("value" in option)) {
      return;
    }
    setShowNotFoundPlaceholder(false);
    watchPromise(
      geocoder
        .geocode({
          placeId: value
        })
        .then((res) => {
          if (res.results.length === 0) {
            console.error("No geocoded results", value);
            return;
          }
          const location = {
            lat: res.results[0].geometry.location.lat(),
            lng: res.results[0].geometry.location.lng()
          };
          setSelectedPlace({
            placeId: value,
            location,
            description: option.label
          });
          lastSelectedPlaceIdRef.current = value;
          onChange(location, {
            placeId: value,
            name: option.data?.structured_formatting.main_text!,
            description: option.label
          });
          if (blurOnSelect) {
            selectRef.current?.blur();
          }
        })
    );
  };

  const handleSearch = (search: string) => {
    if (search.length < 2) {
      // set minimum input length
      return;
    }
    debouncedSearch(search);
  };

  return (
    <Select
      ref={selectRef}
      disabled={disabled}
      showSearch
      value={
        selectedPlace?.placeId
          ? selectedPlace?.placeId
          : displayValue?.displayName
      }
      placeholder={
        placeholder
          ? placeholder
          : showNotFoundPlaceholder
          ? t("placesAutocomplete.couldNotFind")
          : t("placesAutocomplete.whereStay")
      }
      style={{ width: "100%" }}
      defaultActiveFirstOption={true}
      showArrow={false}
      filterOption={false}
      onSearch={handleSearch}
      onChange={handleChange}
      notFoundContent={null}
      loading={isLoading}
      options={
        displayValue && !selectedPlace?.placeId
          ? [
              ...options,
              {
                value: displayValue.displayName,
                label: displayValue.displayName
              }
            ]
          : options
      }
      allowClear={allowClear}
      status={error ? "error" : undefined}
      size={size}
      $fontSize={size === "large" ? antToken.fontSizeLG : undefined}
    />
  );
};

export default PlacesAutocomplete;
